'use client';

import { useMemo, useState } from 'react';
import { BellRing, Check, Loader2, Plus, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ALERT_CADENCES, digestFrequencyFor } from '@/lib/alerts-schemas';
import type {
  ListSegmentConfig,
  ListWatchConfig,
  ListWatchCriterion,
} from '@/lib/backend-api';
import {
  type AlertQuoteInput,
  InsufficientCreditsError,
  useAlertSubscriptions,
} from '@/hooks/use-alerts';
import { useSession } from '@/hooks/use-session';
import { AlertQuoteLine } from '@/new_components/alerts/AlertQuoteLine';
import { cadenceLabel } from '@/new_components/alerts/alert-format';
import { useInsufficientCreditsToast } from '@/new_components/alerts/use-insufficient-credits-toast';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Stepper,
  StepperContent,
  StepperIndicator,
  StepperItem,
  StepperNav,
  StepperPanel,
  StepperSeparator,
  StepperTitle,
  StepperTrigger,
} from '@/components/ui/stepper';

/**
 * The criteria picker, grouped.
 *
 * Fourteen checkboxes in one flat column is a wall — so they are grouped by
 * the question they answer, each group collapsible, with a filter box and a
 * "recommended" preset for the common case. `recommended` marks the five
 * that matter to most people watching a portfolio: who owns it, who runs
 * it, whether it is in trouble, and whether the numbers moved.
 *
 * Order within a group is by how often the change actually happens, so the
 * useful boxes are the ones nearest the top.
 */
const CRITERION_GROUPS: {
  id: string;
  labelKey: string;
  labelFallback: string;
  criteria: {
    id: ListWatchCriterion;
    labelKey: string;
    labelFallback: string;
    hintKey: string;
    hintFallback: string;
    /** Hint for a company watch, where the list wording would be wrong. */
    companyHintKey?: string;
    recommended?: boolean;
  }[];
}[] = [
  {
    id: 'risk',
    labelKey: 'lists.watch.groups.risk',
    labelFallback: 'Risk & legal status',
    criteria: [
      {
        id: 'legal_status_change',
        labelKey: 'lists.watch.criteria.legalStatus',
        labelFallback: 'Insolvency or liquidation',
        hintKey: 'lists.watch.criteria.legalStatusHint',
        hintFallback:
          'The company becomes insolvent, is dissolved, enters liquidation — or recovers.',
        recommended: true,
      },
    ],
  },
  {
    id: 'people',
    labelKey: 'lists.watch.groups.people',
    labelFallback: 'Ownership & management',
    criteria: [
      {
        id: 'shareholder_change',
        labelKey: 'lists.watch.criteria.shareholder',
        labelFallback: 'Shareholders',
        hintKey: 'lists.watch.criteria.shareholderHint',
        hintFallback: 'A new shareholder list is filed.',
        recommended: true,
      },
      {
        id: 'management_change',
        labelKey: 'lists.watch.criteria.management',
        labelFallback: 'Management',
        hintKey: 'lists.watch.criteria.managementHint',
        hintFallback: 'A managing director or board member joins or leaves.',
        recommended: true,
      },
    ],
  },
  {
    id: 'financials',
    labelKey: 'lists.watch.groups.financials',
    labelFallback: 'Financials',
    criteria: [
      {
        id: 'filing_change',
        labelKey: 'lists.watch.criteria.filing',
        labelFallback: 'New annual accounts',
        hintKey: 'lists.watch.criteria.filingHint',
        hintFallback: 'A new financial statement is published.',
        recommended: true,
      },
      {
        id: 'revenue_change',
        labelKey: 'lists.watch.criteria.revenue',
        labelFallback: 'Revenue',
        hintKey: 'lists.watch.criteria.revenueHint',
        hintFallback: 'The reported revenue figure changes.',
        recommended: true,
      },
      {
        id: 'profit_change',
        labelKey: 'lists.watch.criteria.profit',
        labelFallback: 'Profit',
        hintKey: 'lists.watch.criteria.profitHint',
        hintFallback: 'The reported profit or loss changes.',
      },
      {
        id: 'assets_change',
        labelKey: 'lists.watch.criteria.assets',
        labelFallback: 'Total assets',
        hintKey: 'lists.watch.criteria.assetsHint',
        hintFallback:
          'The balance-sheet total changes — reported by far more companies than revenue.',
      },
      {
        id: 'employee_change',
        labelKey: 'lists.watch.criteria.employees',
        labelFallback: 'Employees',
        hintKey: 'lists.watch.criteria.employeesHint',
        hintFallback: 'The reported headcount changes.',
      },
    ],
  },
  {
    id: 'activity',
    labelKey: 'lists.watch.groups.activity',
    labelFallback: 'Business activity',
    criteria: [
      {
        id: 'tender_change',
        labelKey: 'lists.watch.criteria.tender',
        labelFallback: 'Public tenders',
        hintKey: 'lists.watch.criteria.tenderHint',
        hintFallback: 'The company wins a public contract or appears as a buyer.',
      },
      {
        id: 'industry_change',
        labelKey: 'lists.watch.criteria.industry',
        labelFallback: 'Industry',
        hintKey: 'lists.watch.criteria.industryHint',
        hintFallback: 'The classified industry or WZ/NACE code changes.',
      },
      {
        id: 'website_change',
        labelKey: 'lists.watch.criteria.website',
        labelFallback: 'Website',
        hintKey: 'lists.watch.criteria.websiteHint',
        hintFallback: 'The company website we resolved changes.',
      },
    ],
  },
  {
    id: 'profile',
    labelKey: 'lists.watch.groups.profile',
    labelFallback: 'Company profile',
    criteria: [
      {
        id: 'name_change',
        labelKey: 'lists.watch.criteria.name',
        labelFallback: 'Name',
        hintKey: 'lists.watch.criteria.nameHint',
        hintFallback: 'The company is renamed.',
        companyHintKey: 'lists.watch.criteria.nameHintCompany',
      },
      {
        id: 'address_change',
        labelKey: 'lists.watch.criteria.address',
        labelFallback: 'Registered seat',
        hintKey: 'lists.watch.criteria.addressHint',
        hintFallback: 'The company moves to another city or postcode.',
      },
      {
        id: 'segment_change',
        labelKey: 'lists.watch.criteria.segment',
        labelFallback: 'Segment change',
        hintKey: 'lists.watch.criteria.segmentHint',
        hintFallback:
          'A company moves between the segments defined on this list.',
      },
    ],
  },
  {
    // The three document-arrival criteria. Categorically different from
    // everything above: those compare two observed *values*, these fire when
    // a document you can open actually lands, and carry its file id.
    //
    // They must be listed here even though the wizard was built for lists.
    // A company watch created by the star button subscribes to exactly these
    // three, so a picker that could not show them would silently clear them
    // the first time someone opened it and pressed save.
    id: 'documents',
    labelKey: 'lists.watch.groups.documents',
    labelFallback: 'New documents',
    criteria: [
      {
        id: 'new_balance_sheet',
        labelKey: 'lists.watch.criteria.newBalanceSheet',
        labelFallback: 'Annual accounts filed',
        hintKey: 'lists.watch.criteria.newBalanceSheetHint',
        hintFallback:
          'A new Unternehmensregister filing is available to open — not just a changed figure.',
        recommended: true,
      },
      {
        id: 'new_cd_entry',
        labelKey: 'lists.watch.criteria.newCdEntry',
        labelFallback: 'New register entry',
        hintKey: 'lists.watch.criteria.newCdEntryHint',
        hintFallback:
          'Anything the Handelsregister records — every Eintragung, whatever it changed.',
      },
      {
        id: 'new_gesellschafterliste',
        labelKey: 'lists.watch.criteria.newGesellschafterliste',
        labelFallback: 'Shareholder list filed',
        hintKey: 'lists.watch.criteria.newGesellschafterlisteHint',
        hintFallback:
          'A new Gesellschafterliste document is on file, whether or not the owners changed.',
      },
    ],
  },
];

const ALL_CRITERIA = CRITERION_GROUPS.flatMap((group) => group.criteria);
const RECOMMENDED_CRITERIA = ALL_CRITERIA.filter((c) => c.recommended).map(
  (c) => c.id,
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 5;

/**
 * "Add Watchlist" wizard for a list — two steps in the unused ReUI Stepper
 * inside a Dialog. Step 1 picks the change criteria (Segment Change is
 * disabled until the list has segments); step 2 sets the email recipients,
 * the cadence (Stündlich [`immediately`] / Täglich / Wöchentlich / Monatlich) and shows what
 * the alert costs in credits per day before it is saved. Body mounts only
 * while open, so state resets for free.
 *
 * A 402 from `onSave` (the alert cannot be prepaid) arrives as
 * `InsufficientCreditsError` and is answered here with the top-up prompt;
 * the dialog stays open so a cheaper cadence can be picked.
 */
export function ListWatchWizard({
  open,
  onOpenChange,
  watch,
  segmentConfig,
  subjectKind = 'list',
  euId,
  listId,
  onSave,
  onDisable,
  isBusy = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  watch: ListWatchConfig | null;
  segmentConfig: ListSegmentConfig | null;
  /**
   * What is being watched. Only the wording differs — a company and a list
   * are the same subscription to the same engine, which is the whole point
   * of the watch layer. Segment Change is hidden for a company because it
   * has no segments, which the existing `hasSegments` gate already handles.
   */
  subjectKind?: 'company' | 'list';
  /** The watched company — prices the alert (`subjectKind='company'`). */
  euId?: string;
  /** The watched list — prices the alert (`subjectKind='list'`). */
  listId?: number;
  onSave: (config: ListWatchConfig) => Promise<unknown>;
  onDisable: () => Promise<unknown>;
  isBusy?: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        {open && (
          <WizardBody
            watch={watch}
            segmentConfig={segmentConfig}
            subjectKind={subjectKind}
            euId={euId}
            listId={listId}
            onSave={onSave}
            onDisable={onDisable}
            onDone={() => onOpenChange(false)}
            isBusy={isBusy}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function WizardBody({
  watch,
  segmentConfig,
  subjectKind,
  euId,
  listId,
  onSave,
  onDisable,
  onDone,
  isBusy,
}: {
  watch: ListWatchConfig | null;
  segmentConfig: ListSegmentConfig | null;
  subjectKind: 'company' | 'list';
  euId?: string;
  listId?: number;
  onSave: (config: ListWatchConfig) => Promise<unknown>;
  onDisable: () => Promise<unknown>;
  onDone: () => void;
  isBusy: boolean;
}) {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const sessionEmail = session?.user?.email ?? null;

  const [step, setStep] = useState(1);
  const [criteria, setCriteria] = useState<Set<ListWatchCriterion>>(
    () => new Set(watch?.criteria ?? []),
  );
  // Not prefilled with the session address any more: an empty list already
  // means "me", resolved at send time, and storing the address explicitly is
  // what kept mailing the old one after an account email change.
  const [recipients, setRecipients] = useState<string[]>(
    () => watch?.recipient_emails ?? [],
  );
  const [emailDraft, setEmailDraft] = useState('');
  const [filter, setFilter] = useState('');
  const initialCadence = watch?.cadence ?? watch?.digest_frequency ?? 'weekly';
  const [cadence, setCadence] = useState<string>(initialCadence);
  const showInsufficient = useInsufficientCreditsToast();

  // Grandfathered (`billing_exempt`) alerts are free until they are upgraded
  // to Stündlich (`immediately`) — the existing subscription says which one this is.
  const { items: alertItems } = useAlertSubscriptions();
  const existingAlert = alertItems.find((sub) =>
    subjectKind === 'company'
      ? sub.subject_type === 'company' && sub.eu_id === euId
      : sub.subject_type === 'list' && sub.list_id === listId,
  );
  const exempt = !!existingAlert?.billing_exempt;
  const exemptEnds =
    exempt && initialCadence !== 'immediately' && cadence === 'immediately';

  const hasSegments = (segmentConfig?.segments?.length ?? 0) > 0;

  // Filter matches label and hint, so "insolven" and "Umsatz" both land —
  // people search for the word they have in mind, not our label.
  const needle = filter.trim().toLowerCase();
  const hintOf = (c: (typeof ALL_CRITERIA)[number]) =>
    subjectKind === 'company' && c.companyHintKey
      ? t(c.companyHintKey, c.hintFallback)
      : t(c.hintKey, c.hintFallback);
  const visibleGroups = CRITERION_GROUPS.map((group) => {
    // A company has no segments, so Segment Change is not offered at all
    // (for a list without segments it shows disabled, with the reason).
    const offered = group.criteria.filter(
      (c) => c.id !== 'segment_change' || subjectKind !== 'company',
    );
    return {
      ...group,
      criteria: needle
        ? offered.filter((c) =>
            [t(c.labelKey, c.labelFallback), hintOf(c)].some((text) =>
              text.toLowerCase().includes(needle),
            ),
          )
        : offered,
    };
  }).filter((group) => group.criteria.length > 0);

  const toggleCriterion = (id: ListWatchCriterion) => {
    setCriteria((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addRecipient = () => {
    const email = emailDraft.trim().toLowerCase();
    if (!EMAIL_RE.test(email) || recipients.includes(email)) return;
    if (recipients.length >= MAX_RECIPIENTS) return;
    setRecipients([...recipients, email]);
    setEmailDraft('');
  };

  const submit = async () => {
    try {
      await onSave({
        enabled: true,
        criteria: Array.from(criteria),
        recipient_emails: recipients,
        // `digest_frequency` stays for the legacy columns; `cadence` is
        // what the unified dispatcher and the billing read.
        digest_frequency: digestFrequencyFor(cadence),
        cadence: cadence as ListWatchConfig['cadence'],
      });
      onDone();
    } catch (error) {
      if (error instanceof InsufficientCreditsError) {
        showInsufficient(error.needed);
        return;
      }
      toast.error(t('alerts.dialog.errorGeneric'), { position: 'top-center' });
    }
  };

  const criteriaKey = Array.from(criteria).sort().join(',');
  const quoteInput = useMemo<AlertQuoteInput | null>(() => {
    if (step !== 2 || criteriaKey === '') return null;
    const criteriaList = criteriaKey.split(',');
    if (subjectKind === 'company') {
      return euId
        ? { subject_type: 'company', eu_id: euId, cadence, criteria: criteriaList }
        : null;
    }
    return listId != null
      ? { subject_type: 'list', list_id: listId, cadence, criteria: criteriaList }
      : null;
  }, [step, criteriaKey, subjectKind, euId, listId, cadence]);

  const step1Valid = criteria.size > 0;
  // Empty is valid and means the account owner's address, resolved when the
  // digest is sent — which is the only spelling that follows the account
  // instead of freezing whatever was prefilled here. Requiring one is what
  // left users mailing an address they had since changed.
  const step2Valid = true;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <BellRing className="size-4" />
          {subjectKind === 'company'
            ? t('lists.watch.titleCompany', 'Alerts for this company')
            : t('lists.watch.title', 'Watch this list')}
        </DialogTitle>
        <DialogDescription>
          {subjectKind === 'company'
            ? t(
                'lists.watch.descriptionCompany',
                'Get notified when this company changes. Alerts appear in the app immediately; emails go out as a digest.',
              )
            : t(
                'lists.watch.description',
                'Get notified when companies on this list change. Alerts appear in the app immediately; emails go out as a digest.',
              )}
        </DialogDescription>
      </DialogHeader>

      <Stepper value={step} onValueChange={setStep} className="space-y-4">
        <StepperNav>
          {/* The separator reads the step's context, so it must render
              inside a StepperItem, not between them. */}
          <StepperItem step={1}>
            <StepperTrigger>
              <StepperIndicator>1</StepperIndicator>
              <StepperTitle>
                {t('lists.watch.stepCriteria', 'What to watch')}
              </StepperTitle>
            </StepperTrigger>
            <StepperSeparator />
          </StepperItem>
          <StepperItem step={2} disabled={!step1Valid}>
            <StepperTrigger>
              <StepperIndicator>2</StepperIndicator>
              <StepperTitle>
                {t('lists.watch.stepDelivery', 'Notifications')}
              </StepperTitle>
            </StepperTrigger>
          </StepperItem>
        </StepperNav>

        <StepperPanel>
          <StepperContent value={1} className="space-y-3">
            {/* Filter + presets. With fourteen criteria the fastest path for
                most people is "recommended", and for the rest it is typing
                the word they have in mind. */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-40 flex-1">
                <Search className="absolute left-2.5 top-2 size-3.5 text-muted-foreground" />
                <Input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder={t(
                    'lists.watch.filterPlaceholder',
                    'Filter alerts…',
                  )}
                  className="h-8 pl-8 text-xs"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() =>
                  setCriteria(
                    new Set(
                      RECOMMENDED_CRITERIA.filter(
                        (id) => id !== 'segment_change' || hasSegments,
                      ),
                    ),
                  )
                }
              >
                {t('lists.watch.selectRecommended', 'Recommended')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-muted-foreground"
                onClick={() => setCriteria(new Set())}
                disabled={criteria.size === 0}
              >
                {t('lists.watch.clearAll', 'Clear')}
              </Button>
            </div>

            <div className="max-h-[22rem] space-y-3 overflow-y-auto pr-1">
              {visibleGroups.map((group) => {
                const groupIds = group.criteria
                  .filter((c) => c.id !== 'segment_change' || hasSegments)
                  .map((c) => c.id);
                const allOn =
                  groupIds.length > 0 &&
                  groupIds.every((id) => criteria.has(id));
                return (
                  <div key={group.id} className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {t(group.labelKey, group.labelFallback)}
                      </span>
                      <button
                        type="button"
                        className="text-[11px] text-primary hover:underline"
                        onClick={() =>
                          setCriteria((prev) => {
                            const next = new Set(prev);
                            for (const id of groupIds) {
                              if (allOn) next.delete(id);
                              else next.add(id);
                            }
                            return next;
                          })
                        }
                      >
                        {allOn
                          ? t('lists.watch.groupNone', 'none')
                          : t('lists.watch.groupAll', 'all')}
                      </button>
                    </div>
                    {group.criteria.map((criterion) => {
                      const disabled =
                        criterion.id === 'segment_change' && !hasSegments;
                      return (
                        <label
                          key={criterion.id}
                          className={cn(
                            'flex items-start gap-3 rounded-md border border-border px-3 py-2',
                            disabled
                              ? 'opacity-50 cursor-not-allowed'
                              : 'cursor-pointer hover:bg-muted/40',
                          )}
                        >
                          <Checkbox
                            checked={criteria.has(criterion.id)}
                            disabled={disabled}
                            onCheckedChange={() => toggleCriterion(criterion.id)}
                            className="mt-0.5"
                          />
                          <span className="min-w-0">
                            <span className="flex items-center gap-1.5 text-sm font-medium">
                              {t(criterion.labelKey, criterion.labelFallback)}
                              {criterion.recommended && (
                                <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-normal text-primary">
                                  {t('lists.watch.recommended', 'recommended')}
                                </span>
                              )}
                            </span>
                            <span className="block text-xs text-muted-foreground">
                              {disabled
                                ? t(
                                    'lists.watch.criteria.segmentDisabled',
                                    'Define segments on this list first.',
                                  )
                                : hintOf(criterion)}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                );
              })}
              {visibleGroups.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  {t('lists.watch.noMatches', 'No alerts match that filter.')}
                </p>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              {t('lists.watch.selectedCount', {
                count: criteria.size,
                defaultValue: '{{count}} alerts selected',
              })}
            </p>
          </StepperContent>

          <StepperContent value={2} className="space-y-4">
            <div>
              <Label className="text-sm font-medium">
                {t('lists.watch.recipients', 'Email recipients')}
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                {recipients.length === 0
                  ? t(
                      'lists.watch.recipientsOwnerOnly',
                      'The digest goes to your account email. Add addresses to send it to colleagues as well.',
                    )
                  : t('lists.watch.recipientsHint', {
                      count: MAX_RECIPIENTS,
                      defaultValue:
                        'Up to {{count}} addresses receive the digest, alongside your account email.',
                    })}
              </p>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {recipients.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs"
                  >
                    {email}
                    {email === sessionEmail && (
                      <span className="text-muted-foreground">
                        {t('lists.watch.recommendedTag', '(you)')}
                      </span>
                    )}
                    <button
                      type="button"
                      aria-label={t('lists.watch.removeRecipient', 'Remove')}
                      onClick={() =>
                        setRecipients(recipients.filter((e) => e !== email))
                      }
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addRecipient();
                    }
                  }}
                  placeholder={
                    sessionEmail ??
                    t('lists.watch.emailPlaceholder', 'name@company.com')
                  }
                  type="email"
                  className="h-8"
                  disabled={recipients.length >= MAX_RECIPIENTS}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addRecipient}
                  disabled={
                    !EMAIL_RE.test(emailDraft.trim().toLowerCase()) ||
                    recipients.length >= MAX_RECIPIENTS
                  }
                >
                  <Plus className="size-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">
                {t('alerts.cadence.label')}
              </Label>
              <RadioGroup
                value={cadence}
                onValueChange={setCadence}
                className="mt-2 grid grid-cols-2 gap-2"
              >
                {ALERT_CADENCES.map((value) => (
                  <label
                    key={value}
                    className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted/40 lg:min-h-9"
                  >
                    <RadioGroupItem value={value} />
                    {cadenceLabel(value, t)}
                  </label>
                ))}
              </RadioGroup>
              <p className="text-xs text-muted-foreground mt-2">
                {t(
                  'lists.watch.instantNote',
                  'Emails go out only when something actually changed — never an empty digest. In-app notifications are instant either way.',
                )}
              </p>
            </div>

            <AlertQuoteLine
              input={quoteInput}
              exempt={exempt}
              exemptEnds={exemptEnds}
              isExisting={!!watch?.enabled}
            />
          </StepperContent>
        </StepperPanel>
      </Stepper>

      <DialogFooter className="gap-2">
        {watch?.enabled && (
          <Button
            variant="ghost"
            className="me-auto text-destructive"
            disabled={isBusy}
            onClick={async () => {
              await onDisable();
              onDone();
            }}
          >
            {subjectKind === 'company'
              ? t('lists.watch.disableCompany', 'Stop watching this company')
              : t('lists.watch.disable', 'Stop watching')}
          </Button>
        )}
        {step === 1 ? (
          <Button onClick={() => setStep(2)} disabled={!step1Valid}>
            {t('lists.watch.next', 'Next')}
          </Button>
        ) : (
          <>
            <Button variant="outline" onClick={() => setStep(1)}>
              {t('lists.watch.back', 'Back')}
            </Button>
            <Button
              onClick={() => void submit()}
              disabled={!step1Valid || !step2Valid || isBusy}
            >
              {isBusy ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Check className="size-4" />
              )}
              {watch?.enabled
                ? t('common.buttons.save', 'Save')
                : t('lists.watch.save', 'Activate alerts')}
            </Button>
          </>
        )}
      </DialogFooter>
    </>
  );
}
